import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRight, Github, Linkedin, Code2, Sparkles } from 'lucide-react';
import { personalInfo, heroCopy } from '../data/mock';
import { Button } from './ui/button';

const BINARY_INTERVAL_MS = 220;
const BINARY_INTERVAL_HOVER_MS = 42;

/** Ray-cast point-in-polygon (works for concave shapes like a 5-point star). */
function pointInPolygon(px, py, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x;
    const yi = poly[i].y;
    const xj = poly[j].x;
    const yj = poly[j].y;
    if ((yi > py) !== (yj > py)) {
      const xInt = ((xj - xi) * (py - yi)) / (yj - yi) + xi;
      if (px < xInt) inside = !inside;
    }
  }
  return inside;
}

/** Regular 5-point star pointing up; `outerR` = tip radius, `innerR` = inner re-entrant corners. */
function buildStarPolygon(cx, cy, outerR, innerR) {
  const verts = [];
  for (let i = 0; i < 10; i++) {
    const angle = -Math.PI / 2 + (i * Math.PI) / 5;
    const rad = i % 2 === 0 ? outerR : innerR;
    verts.push({
      x: cx + rad * Math.cos(angle),
      y: cy + rad * Math.sin(angle),
    });
  }
  return verts;
}

const BinaryArt = () => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const dimsRef = useRef({ w: 0, h: 0, dpr: 1 });

  const resizeCanvas = useCallback(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = wrap.clientWidth;
    const h = wrap.clientHeight;
    if (w < 1 || h < 1) return;

    dimsRef.current = { w, h, dpr };
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
  }, []);

  useEffect(() => {
    resizeCanvas();
    const ro = new ResizeObserver(() => resizeCanvas());
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener('resize', resizeCanvas);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [resizeCanvas]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const draw = () => {
      const { w, h } = dimsRef.current;
      if (w < 1 || h < 1) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const { dpr } = dimsRef.current;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const isDark = document.documentElement.classList.contains('dark');
      ctx.fillStyle = isDark ? 'rgba(235, 210, 175, 0.72)' : 'rgb(32, 24, 18)';

      const spacing = Math.max(9, Math.round(Math.min(w, h) * 0.015));
      const fontPx = Math.max(9, Math.round(spacing * 0.94));
      ctx.font = `${fontPx}px "Space Mono", ui-monospace, monospace`;

      const m = Math.min(w, h);
      const outerR = m * 0.245;
      const cx = w * 0.5;
      const cy = h * 0.48;
      const pad = outerR * 1.12;
      const minX = Math.max(0, cx - pad);
      const maxX = Math.min(w, cx + pad);
      const minY = Math.max(0, cy - pad);
      const maxY = Math.min(h, cy + pad);
      const binary = ['0', '1'];
      const starPoly = buildStarPolygon(cx, cy, outerR, outerR * 0.382);

      for (let y = minY; y < maxY; y += spacing) {
        for (let x = minX; x < maxX; x += spacing) {
          if (!pointInPolygon(x, y, starPoly)) continue;
          const edge = (x - minX) / Math.max(1, maxX - minX);
          const fade = (isDark ? 0.55 : 0.72) + edge * (isDark ? 0.45 : 0.28);
          ctx.globalAlpha = fade;
          ctx.fillText(binary[Math.floor(Math.random() * 2)], x, y);
          ctx.globalAlpha = 1;
        }
      }
      ctx.restore();
    };

    draw();
    const ms = hovered ? BINARY_INTERVAL_HOVER_MS : BINARY_INTERVAL_MS;
    const id = setInterval(draw, ms);
    return () => clearInterval(id);
  }, [hovered, resizeCanvas]);

  return (
    <div
      ref={wrapRef}
      className="absolute inset-y-0 right-0 w-full max-w-[min(92vw,440px)] sm:max-w-[min(62vw,580px)] md:max-w-[min(54vw,640px)] z-[1] pointer-events-auto cursor-default select-none touch-none"
      style={{
        WebkitMaskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.45) 15%, black 48%)',
        maskImage: 'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.45) 15%, black 48%)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-hidden
    >
      <canvas
        ref={canvasRef}
        className="block h-full w-full opacity-100 dark:opacity-[0.96]"
      />
    </div>
  );
};

const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section
      id="hero"
      className="min-h-screen flex items-center justify-center px-6 pt-20 relative overflow-hidden border-b border-stone-400/25 dark:border-stone-500/20"
    >
      <BinaryArt />
      <div className="max-w-7xl w-full relative z-10">
        <div className="max-w-3xl">
          <div className="inline-flex items-center space-x-2 px-5 py-2.5 bg-amber-100/60 dark:bg-amber-900/30 backdrop-blur-sm rounded-full text-sm font-medium text-amber-900 dark:text-amber-200 mb-8 border border-amber-200/50 dark:border-amber-800/50">
            <Sparkles className="w-4 h-4" />
            <span>{heroCopy.badge}</span>
          </div>

          <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-stone-900 dark:text-stone-100 mb-6 leading-tight">
            Ankit Attri
          </h1>

          <div className="space-y-3 mb-8">
            <p className="text-2xl md:text-3xl lg:text-4xl text-stone-800 dark:text-stone-100 font-semibold leading-tight">
              {heroCopy.headline}
            </p>
            <p className="text-lg md:text-xl text-stone-600 dark:text-stone-400 font-medium">
              {heroCopy.subline}
            </p>
          </div>

          <p className="text-lg text-stone-600 dark:text-stone-400 max-w-2xl mb-10 leading-relaxed">
            {heroCopy.intro}
          </p>

          <div className="flex flex-wrap gap-4 mb-12">
            <Button
              onClick={() => scrollToSection('#projects')}
              className="bg-stone-900 hover:bg-stone-800 dark:bg-stone-100 dark:hover:bg-stone-200 text-white dark:text-stone-900 px-8 py-6 text-base rounded-2xl shadow-lg hover:shadow-xl transition-all"
            >
              View projects
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              onClick={() => scrollToSection('#live-signal')}
              variant="secondary"
              className="px-8 py-6 text-base rounded-2xl shadow-md"
            >
              Live signal & dashboard
            </Button>
            <Button
              onClick={() => scrollToSection('#contact')}
              variant="outline"
              className="px-8 py-6 text-base rounded-2xl border-2 border-stone-300 dark:border-stone-700 hover:bg-stone-100 dark:hover:bg-stone-800 backdrop-blur-sm"
            >
              Get in Touch
            </Button>
          </div>

          <div className="flex items-center space-x-6">
            <a
              href={personalInfo.profiles.github}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-xl bg-stone-200/50 dark:bg-stone-800/50 hover:bg-stone-300/70 dark:hover:bg-stone-700/70 transition-colors backdrop-blur-sm"
              aria-label="GitHub"
            >
              <Github className="w-5 h-5 text-stone-700 dark:text-stone-300" />
            </a>
            <a
              href={personalInfo.profiles.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-xl bg-stone-200/50 dark:bg-stone-800/50 hover:bg-stone-300/70 dark:hover:bg-stone-700/70 transition-colors backdrop-blur-sm"
              aria-label="LinkedIn"
            >
              <Linkedin className="w-5 h-5 text-stone-700 dark:text-stone-300" />
            </a>
            <a
              href={personalInfo.profiles.leetcode}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-xl bg-stone-200/50 dark:bg-stone-800/50 hover:bg-stone-300/70 dark:hover:bg-stone-700/70 transition-colors backdrop-blur-sm"
              aria-label="LeetCode"
            >
              <Code2 className="w-5 h-5 text-stone-700 dark:text-stone-300" />
            </a>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16 max-w-4xl">
          {personalInfo.stats.map((stat, index) => (
            <div
              key={index}
              className="p-6 rounded-2xl bg-white/60 dark:bg-stone-800/60 backdrop-blur-md border border-stone-200/50 dark:border-stone-700/50 hover:shadow-lg transition-all hover:-translate-y-1"
            >
              <div className="text-3xl md:text-4xl font-bold text-amber-900 dark:text-amber-200 mb-2">
                {stat.value}
              </div>
              <div className="text-xs text-stone-600 dark:text-stone-400 uppercase tracking-wider">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
